import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import { EPaymentMethod, useLandingPageContext } from "pages/Context/LandinPageContext";
import { Button, Card } from "react-bootstrap";
import { generatePaymentToken, IGeneratePaymentToken } from "../service/efi.service";
import { CreatePix, CreateTicket, IPaymentPixResponse, IPaymentTicketLicitem, IPaymentTicketLicitemResponse, PaymentCard } from "../service/payment.service";
import { IPeriodicTime } from "core/enums/plans.enum";
import { FiBox, FiGlobe, FiLayers, FiStar, FiZap, FiCheck } from "react-icons/fi";
import { IconType } from "react-icons";
import IconCreditCard from "components/Icons/IconCreditCard";
import IconPix from "assets/svgs/IconPix";
import IconBarCode from "components/Icons/IconBarCode";
import { hideLoading, showLoading } from "components/LLoading";
import { LTMTransactionStatus } from "core/enums/transaction";
import LAlert from "components/LAlert";
import { monitorTransaction } from "core/functions/monitorTransaction";

export default function CardConfirmPayment() {
  const { selectedPlanContext, paymentMethodContext, supplierDataContext, setPixCopyAndPasteContext, timeLeftContext, setTimeLeftContext, setCurrentStepContext, setTicketPDF, pixCopyAndPasteContext, quantityOnDemandContext, setIsManualPaymentContext} = useLandingPageContext();
  const quantityForPlan = selectedPlanContext?.maxOnDemandQuantity === 1 ? 1 : quantityOnDemandContext;

  // Gateway caiu no fluxo manual: avança pro sucesso marcando como manual.
  // A tela final (PaymentWithSuccess) mostra "estamos verificando seu pagamento".
  const handleManualPayment = () => {
    setIsManualPaymentContext(true);
    setCurrentStepContext(4);
  };
  const { values, errors, dirty } = useFormikContext<any>();
  const hasErrors = Object.keys(errors).length > 0;
  const disableButton = !dirty || hasErrors || (paymentMethodContext === EPaymentMethod.PIX && !!pixCopyAndPasteContext);
  const requestedInstallments = Number(values?.installmentsNumber || selectedPlanContext?.period.installmentCount || 1);
  const installmentNumber = paymentMethodContext === EPaymentMethod.PIX
    ? 1
    : Number.isFinite(requestedInstallments) && requestedInstallments > 0
      ? requestedInstallments
      : 1;
  const totalAmount = (selectedPlanContext?.allAmount ?? 0) * (
    selectedPlanContext?.period.type === IPeriodicTime.ON_DEMAND ? quantityForPlan : 1
  );
  const formatMoney = (amount: number) => amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const periodLabels: Partial<Record<IPeriodicTime, string>> = {
    [IPeriodicTime.MONTHLY]: "mês",
    [IPeriodicTime.BIMONTHLY]: "bimestre",
    [IPeriodicTime.QUARTERLY]: "trimestre",
    [IPeriodicTime.SEMESTERLY]: "semestre",
    [IPeriodicTime.YEARLY]: "ano",
    [IPeriodicTime.SPECIFIC]: "período",
    [IPeriodicTime.ON_DEMAND]: "processo",
  };
  const periodLabel = selectedPlanContext && periodLabels[selectedPlanContext.period.type];


  let data: IPaymentTicketLicitem = {
    planId: selectedPlanContext?._id || "",
    supplierId: supplierDataContext?.idSupplier ?? "",
    installmentsNumber: values.installmentsNumber,
    isCnpjUsed: Boolean(values.identification),
  };


  if(selectedPlanContext?.period.type === IPeriodicTime.ON_DEMAND) {
    data = {...data, onDemandProcessCount: quantityForPlan};
  }

  const FormPayment = {
    [EPaymentMethod.CREDIT_CARD]: <div className="d-flex gap-1 justify-content-center align-items-center fw-bold"  style={{ fontSize: ".875rem"}}>
      <IconCreditCard />
      <p className="m-0">Cartão de Crédito</p>
    </div>,
    [EPaymentMethod.PIX]: <div className="d-flex gap-1 justify-content-center align-items-center fw-bold"  style={{ fontSize: ".875rem"}}>
      <IconPix width={15} className=""/>
      <p className="m-0">Pix</p>
    </div>,
    [EPaymentMethod.BOLETO]: <div className="d-flex gap-1 justify-content-center align-items-center fw-bold"  style={{ fontSize: ".875rem"}}>
      <IconBarCode/>
      <p className="m-0">Boleto</p>
    </div>,
  }[paymentMethodContext];

  const buttonFinishText = {
    [EPaymentMethod.CREDIT_CARD]: "Confirmar e Pagar",
    [EPaymentMethod.PIX]: "Gerar Pix",
    [EPaymentMethod.BOLETO]: "Gerar Boleto",
  }[paymentMethodContext];

  const paymentInfo = {
    [EPaymentMethod.CREDIT_CARD]: "Seu pagamento será processado no cartão de crédito após a confirmação.",
    [EPaymentMethod.PIX]: "Após gerar o Pix, pague pelo QR Code ou copie o código. Validade de 5 minutos.",
    [EPaymentMethod.BOLETO]: installmentNumber === 1
      ? "Você receberá um boleto para pagamento."
      : `Você receberá ${installmentNumber} boletos mensais para pagamento.`,
  }[paymentMethodContext];
  const installmentDescription = paymentMethodContext === EPaymentMethod.PIX
    ? "Pagamento único via Pix"
    : installmentNumber === 1
      ? "Pagamento à vista"
      : `${installmentNumber}× de ${formatMoney(totalAmount / installmentNumber)}`;

  const iconMap: { [key: string]: IconType } = {
    [IPeriodicTime.ON_DEMAND]: FiBox,
    [IPeriodicTime.MONTHLY]: FiStar,
    [IPeriodicTime.BIMONTHLY]: FiLayers,
    [IPeriodicTime.QUARTERLY]: FiZap,
    [IPeriodicTime.SEMESTERLY]: FiLayers,
    [IPeriodicTime.YEARLY]: FiGlobe,
  };
  
  const IconComponent = selectedPlanContext && iconMap[selectedPlanContext.period.type];


  useEffect(() => {
    if (timeLeftContext && timeLeftContext > 0) {
      const regeneratePix = async () => {
        try {
          showLoading();
          const dynamicData: IPaymentTicketLicitem = {
            planId: selectedPlanContext?._id || "",
            supplierId: supplierDataContext?.idSupplier ?? "",
            installmentsNumber: values.installmentsNumber,
            isCnpjUsed: Boolean(values.identification),
            ...(selectedPlanContext?.period.type === IPeriodicTime.ON_DEMAND && {
              onDemandProcessCount: quantityForPlan,
            }),
          };
  
          const response = await CreatePix(dynamicData);
          setPixCopyAndPasteContext(response.pixCopiaECola);
          const dueDate = new Date(response.dueDate.replace(" ", "T")); 
          const now = new Date();
          const newTimeRemaining = Math.max(Math.floor((dueDate.getTime() - now.getTime()) / 1000), 0);
          setTimeLeftContext(newTimeRemaining);
          await monitorTransaction(response._id, true, paymentMethodContext, setCurrentStepContext);
        } catch (err) {
          LAlert({description: `${err}`});
        } finally {
          hideLoading();
        }
      };

      const timer = setInterval(() => {
        setTimeLeftContext((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            regeneratePix();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeLeftContext, selectedPlanContext, supplierDataContext, values, quantityOnDemandContext, setTimeLeftContext, setPixCopyAndPasteContext, paymentMethodContext, setCurrentStepContext]);



  const handleSubmitCredtiCard = async () => {
    showLoading();
    setIsManualPaymentContext(false);

    let dataPaymentCard: any = {
      planId: selectedPlanContext?._id || "",
      supplierId: supplierDataContext?.idSupplier ?? "",
      installmentsNumber: Number(values?.installmentsNumber),
      reuseCard: values.reuse,
      customerName: values.holderName,
      customerCpf: values.holderDocument,
      // customerBirth: `${values.expiration.slice(0, 2)}/${values.expiration.slice(3, 7)}`
      paymentToken: "",
      cardMask: "",
    };

    const dataGeneratePaymentToken: IGeneratePaymentToken = {
      ...values,
      expirationMonth: values.expiration.slice(0, 2),
      expirationYear: values.expiration.slice(3, 7)
    };


    if(selectedPlanContext?.period.type === IPeriodicTime.ON_DEMAND) {
      dataPaymentCard = {...dataPaymentCard, onDemandProcessCount: quantityForPlan};
    }
  
    try {
      const token = await generatePaymentToken(dataGeneratePaymentToken);
      const response = await PaymentCard({...dataPaymentCard, paymentToken: token.payment_token, cardMask: token.card_mask});

      if (response?.paymentStatus === LTMTransactionStatus.GATEWAY_FAILED) {
        handleManualPayment();
      } else if (response?.paymentStatus === LTMTransactionStatus.PENDING) {
        await monitorTransaction(response._id, false, paymentMethodContext, setCurrentStepContext, handleManualPayment);
      }
    } catch (err) {
      LAlert({description: `${err}`});
    }  finally {
      hideLoading();
    }
  };


  const handleSubmitPixAndTicket = async () => {
    try {
      showLoading();
      setIsManualPaymentContext(false);
      let response: IPaymentPixResponse | IPaymentTicketLicitemResponse | any;
  
      if (paymentMethodContext === EPaymentMethod.PIX) {
        response = await CreatePix(data);

        if (response?.paymentStatus === LTMTransactionStatus.GATEWAY_FAILED) {
          handleManualPayment();
          return;
        }

        setPixCopyAndPasteContext(response.pixCopiaECola);

        // Converte a dueDate para um objeto Date
        const dueDate = new Date(response.dueDate.replace(" ", "T")); // Garante que seja um formato ISO válido
        const now = new Date();

        // Calcula o tempo restante em segundos
        const timeRemaining = Math.max(Math.floor((dueDate.getTime() - now.getTime()) / 1000), 0);
        setTimeLeftContext(timeRemaining);

        if (response?.paymentStatus === LTMTransactionStatus.PENDING) {
          await monitorTransaction(response._id, true, paymentMethodContext, setCurrentStepContext, handleManualPayment);
        }
      } else {
        response = await CreateTicket(data);

        if (response?.paymentStatus === LTMTransactionStatus.GATEWAY_FAILED) {
          handleManualPayment();
          return;
        }

        setTicketPDF(response?.pdf);
        setCurrentStepContext(4);
        if (response?.pdf) window.open(response.pdf, "_blank");
      }

    } catch (err) {
      LAlert({description: `${err}`});
    } finally {
      hideLoading();
    }
  };

  const handleSubmit = {
    [EPaymentMethod.CREDIT_CARD]: handleSubmitCredtiCard,
    [EPaymentMethod.PIX]: handleSubmitPixAndTicket,
    [EPaymentMethod.BOLETO]: handleSubmitPixAndTicket,
  }[paymentMethodContext];

  return (
    <Card className="payment-summary mb-6">
      <Card.Body>
        <h3 className="payment-summary-title">Resumo do pedido</h3>
        {selectedPlanContext ? (
          <>
            <div className="payment-summary-plan">
              {IconComponent && <span className="payment-summary-icon" aria-hidden="true"><IconComponent /></span>}
              <div>
                <h4>{selectedPlanContext.name}</h4>
                <p>{periodLabel && `Plano por ${periodLabel}`}</p>
              </div>
            </div>
            <ul className="payment-summary-benefits">
              {(selectedPlanContext.benefit ?? []).map((item, index) => (
                <li key={index}><span className="payment-summary-check" aria-hidden="true"><FiCheck /></span><span>{item}</span></li>
              ))}
            </ul>
            <div className="payment-summary-method">
              <span className="payment-summary-label">Forma de pagamento</span>
              {FormPayment}
            </div>
            <div className="payment-summary-total">
              <div><span>Total a pagar</span><strong>{formatMoney(totalAmount)}</strong></div>
              <p>{installmentDescription}</p>
              {selectedPlanContext.period.type === IPeriodicTime.ON_DEMAND && quantityForPlan > 1 && (
                <p>{quantityForPlan} processos × {formatMoney(selectedPlanContext.allAmount)}</p>
              )}
            </div>
            {selectedPlanContext.discount !== "0" && (selectedPlanContext.savingsAmount ?? 0) > 0 && (
              <div className="payment-summary-savings">
                <strong>{selectedPlanContext.discount}</strong>
                <span>Você economiza {formatMoney(selectedPlanContext.savingsAmount ?? 0)} {periodLabel && `no ${periodLabel}`}</span>
              </div>
            )}
            <p className="payment-summary-info">{paymentInfo}</p>
            <Button
              variant="primary"
              className="site-button site-button-primary w-100"
              onClick={disableButton ? () => {} : handleSubmit}
              disabled={disableButton}
            >
              {buttonFinishText}
            </Button>
          </>
        ) : (
          <p>Nenhum plano selecionado.</p>
        )}
      </Card.Body>
    </Card>
  );
}
